import { Component, Input, Output, EventEmitter, ViewChild, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ValidationDirective } from '../validation/validation.directive';
import {  MatPaginator, MatPaginatorModule } from '@angular/material/paginator';


@Component({
    selector: 'mi-table',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ValidationDirective,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        MatSortModule,
        MatTableModule,
        FormsModule,
        MatTooltipModule,
        MatPaginatorModule,
        MatPaginator
    ],
    templateUrl: './mi-table.component.html',
    styleUrls: ['./mi-table.component.css']
})
export class MiTableComponent implements AfterViewInit, OnChanges {
  @Input() isSearchRequired:boolean=true;
  @Input() data: any[] = [];
  @Input() searchText: string = '';
  @Input() actions: boolean = false;
  @Input() validationRules: { [key: string]: any[] } = {};
  @Input() nonEditableFields: string[] = [];
  @Input() allowedActions: string[] = ['edit', 'delete', 'view', 'download', 'stamp'];
  @Input() isMaster: boolean = false;

  @Output() actionEvent = new EventEmitter<{ action: string, row: any }>();
  @Output() rowUpdated = new EventEmitter<any>();
  @Output() rowEditCanceled = new EventEmitter<any>();
  @Output() validationError = new EventEmitter<string>();

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;


  editRowForm: FormGroup | null = null;
  editingRow: any = null;
  allowEditMaster: boolean = false;

  

  dataSource = new MatTableDataSource<any>();

  constructor(private fb: FormBuilder) {}

 


ngAfterViewInit(): void {
  this.dataSource.sort = this.sort;
  this.dataSource.paginator = this.paginator;
  
}

ngOnChanges(changes: SimpleChanges): void {
  if (changes['data'] || changes['searchText']) {
    const filteredData = this.getFilteredData();
    this.dataSource.data = filteredData;

    // 🔹 Rebind paginator & sort after data changes
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }
}



  getColumns(): string[] {
  return this.data.length > 0
    ? Object.keys(this.data[0]).filter(k =>
        !k.startsWith('_') && k !== 'isEditing' && k !== '_original'
      )
    : [];
}


  getFilteredData(): any[] {
    if (!this.searchText) return this.data;
    const filterText = this.searchText.toLowerCase();
    return this.data.filter(item =>
      Object.values(item).some(val =>
        val?.toString().toLowerCase().includes(filterText)
      )
    );
  }

  onAction(action: string, row: any): void {
    this.actionEvent.emit({ action, row });
  }

  startEdit(row: any): void {
    row._original = { ...row };
    row.isEditing = true;
    this.editingRow = row;

    const formGroup: { [key: string]: FormControl } = {};
    this.getColumns().forEach(col => {
      const validators = this.validationRules[col] || this.validationRules[col.toLowerCase()] || [];
     formGroup[col] = new FormControl(
        { value: row[col], disabled: this.nonEditableFields.includes(col) },
        validators
      );
    });

    this.editRowForm = this.fb.group(formGroup);
     // ✅ Trigger re-validation of 'Remit Address' when 'Check Request' changes
  const checkRequestControl = this.editRowForm.get('Check Request');
  const remitAddressControl = this.editRowForm.get('Remit Address');

  if (checkRequestControl && remitAddressControl) {
    checkRequestControl.valueChanges.subscribe(() => {
      remitAddressControl.updateValueAndValidity();
      checkRequestControl.updateValueAndValidity();
    });
  }
  }

  saveEdit(row: any): void {
    if (!this.editRowForm) return;

    for (const key of Object.keys(this.editRowForm.controls)) {
      const control = this.editRowForm.get(key);
      control?.markAsTouched();
      control?.markAsDirty();
    }

    if (this.editRowForm.invalid) {
      this.validationError.emit('Please fix validation errors before saving');
      return;
    }

    Object.keys(this.editRowForm.controls).forEach(key => {
      row[key] = this.editRowForm?.get(key)?.value;
    });

    row.isEditing = false;
    // delete row._original;
    row._original = true;
    this.editingRow = null;
    this.editRowForm = null;
    this.rowUpdated.emit(row);
  }

  cancelEdit(row: any): void {
    Object.assign(row, row._original);
    row.isEditing = false;
    row._original = true;
    // delete row._original;
    this.editingRow = null;
    this.editRowForm = null;
    this.rowEditCanceled.emit(row);
  }

  getFormControl(column: string): FormControl | null {
    return this.editRowForm?.get(column) as FormControl ?? null;
  }
getInvalidCellStatus(row: any, column: string): boolean {
  const rules = this.validationRules[column] || this.validationRules[column.toLowerCase()];

  // ✅ Only use FormControl if this is the row being edited
  if (row === this.editingRow) {
    const control = this.getFormControl(column);
    if (control) return control.invalid;
  }

  // ✅ Otherwise, fallback to static read-mode validation
  const value = row[column];
  const fallbackControl = new FormControl(value, rules);
  if (fallbackControl.invalid) return true;

  // ✅ Backend validation
  const backendMap: { [key: string]: string } = {
    'Billing Site': 'billingSiteIsValid',
    'Customer Name': 'customerIsValid',
    // Add more mappings if needed
  };

  const backendKey = backendMap[column];
  if (backendKey && row._backendValidation && row._backendValidation[backendKey] === false) {
    return true;
  }

  // ✅ Custom logic: Check Request "Yes" requires Remit Address
  const checkRequest = (row['Check Request'] || '').toString().trim().toLowerCase();
  const remitAddress = (row['Remit Address'] || '').toString().trim();

  if (checkRequest === 'yes' && column === 'Remit Address' && !remitAddress) {
    return true;
  }

  return false;
}


getValidationMessage(row: any, column: string): string {
  const control = this.getFormControl(column);
  const rules = this.validationRules[column] || this.validationRules[column.toLowerCase()];

  if (control) {
    if (control.valid) return '';
    if (column === 'Remit Address' && control.errors?.['remitRequired']) {
      return `Remit Address is required when Check Request is "Yes"`;
    }
    if (column === 'Check Request' && control.errors?.['invalidCheckRequest']) {
      return `Check Request should be "Yes" or "No"`;
    }
    if (control.errors?.['required']) return `${column} is required`;

    if (control.errors?.['pattern']) return `${column} has invalid format`;
    if (control.errors?.['maxlength']) {
      const maxLen = control.errors['maxlength'].requiredLength;
      return `${column} cannot exceed ${maxLen} characters`;
    }
    return 'Invalid';
  }

  const rawValue = row[column];
  const fallbackControl = new FormControl(rawValue, rules);
  if (fallbackControl.valid) return '';
  if (fallbackControl.errors?.['required']) return `${column} is required`;
  if (fallbackControl.errors?.['pattern']) return `${column} has invalid format`;
  if (fallbackControl.errors?.['maxlength']) {
    const maxLen = fallbackControl.errors['maxlength'].requiredLength;
    return `${column} cannot exceed ${maxLen} characters`;
  }
  return 'Invalid';
}

applySearch() {
  const filteredData = this.getFilteredData();
  this.dataSource.data = filteredData;
}
canEdit(row: any): boolean {
  const allowedStatuses = ['--','submitted', 'need more info', 'need more information', 'gbs need more information', 'received', null, undefined];
  const statusKeys = ['status', 'statusname', 'mistatus', 'mi status', 'gbsstatus', 'gbs status'];

  const normalizedRow: { [key: string]: any } = {};
  Object.keys(row).forEach(key => {
    const normalizedKey = key.trim().toLowerCase().replace(/\s+/g, '');
    normalizedRow[normalizedKey] = row[key];
  });

  let status: any = '';
  let foundStatusKey = false;
  for (const key in normalizedRow) {
    if (statusKeys.some(sk => sk.replace(/\s+/g, '') === key)) {
      status = normalizedRow[key];
      foundStatusKey = true;
      break;
    }
  }

  let isAllowed = false;
  if (!foundStatusKey) {
    isAllowed = true;
  } else if (status === null || status === undefined) {
    isAllowed = true;
  } else if (typeof status === 'string') {
    const normalizedStatus = status.trim().toLowerCase();
    isAllowed = allowedStatuses
      .filter(s => typeof s === 'string')
      .some(s => normalizedStatus.includes(s.toLowerCase()));
  }

  // GBS special case
  const gbsStatus = String(normalizedRow['gbsstatus'] || '').trim().toLowerCase();
  const gbsNeedMoreInfo = gbsStatus === 'gbs need more information';

  // Detect master vs detail row
  const hasAdjReqId = normalizedRow.hasOwnProperty('adjreqid') && normalizedRow['adjreqid'] != null;
  const hasDetailsId = normalizedRow.hasOwnProperty('_detailsid') && normalizedRow['_detailsid'] != null;

  // Master row: disable if Cancelled OR First Level Rejected
  if (hasAdjReqId && !hasDetailsId) {
    const normalizedStatus = String(status || '').trim().toLowerCase();
    return !this.editingRow && normalizedStatus !== 'cancelled' && normalizedStatus !== 'first level rejected' && normalizedStatus!== 'completed';
  }

  // Detail row: follow existing rules
  if (hasAdjReqId && hasDetailsId) {
    return !this.editingRow && (isAllowed || gbsNeedMoreInfo);
  }

  // Default
  return !this.editingRow && isAllowed;
}

canEditMaster(row:any): boolean {
   //if(!this.isMaster){
    // const filteredData = dataSource.filteredData;
    
    // // Check if any row has "MI Status" = "Submitted" or "GBS Status" = "GBS Need More Information"
    // this.allowEditMaster= filteredData.some((item: any) => {
    //   const miStatus = String(item['MI Status'] || '').trim().toLowerCase();
    //   const gbsStatus = String(item['GBS Status'] || '').trim().toLowerCase();
    //   const status = String(item['status'] || '').trim().toLowerCase();
    //   return status === 'submitted' || miStatus === '--' || miStatus === 'submitted' || miStatus === 'need more information'|| gbsStatus === 'gbs need more information';
    // });
   //}
   if(row.Status=="Submitted"){
     return true;
   }else{
    return row._isAllowEdit;
   }   
}
canDeleteMaster(row:any): boolean {
  if(row.Status=="Submitted"){
    return true;
  }else{
   return false;
  }   
}
canDeleteInvoice(row:any): boolean {
  if(row._statusId=="1"){
    return true;
  }else{
   return false;
  }   
}
formatNegativeAmount(col: string, value: any) {
  const negativeCols = ['Amount', 'Net Amount', 'adjNetAmount', 'totalCorrectCharges'];
  if (!negativeCols.includes(col)) {
    return { text: value ?? '', style: { color: 'inherit' } };
  }

  // Parse number from any format
  let num = Number(String(value).replace(/[^0-9.-]/g, '') || 0);

  if (!isNaN(num) && num < 0) {
    return {
      text: `$(${Math.abs(num).toLocaleString(undefined, { minimumFractionDigits: 2 })})`,
      style: { color: 'red', 'font-weight': 'bold' }
    };
  }

  return {
    text: `$${num.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
    style: { color: 'inherit' }
  };
}

}

